import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchProjects, fetchStarredProjects, toggleProjectStar } from '../store/slices/projectsSlice';
import { toast } from 'react-toastify';
import { generateAndDownloadReport } from '../services/reporting';
import GlobalTicketSearch from '../components/common/GlobalTicketSearch';
import api from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { projects, loading, pagination, starredProjects } = useSelector((state) => state.projects);
  const [generatingForProjectId, setGeneratingForProjectId] = React.useState(null);
  const [metrics, setMetrics] = React.useState(null);
  const [metricsLoading, setMetricsLoading] = React.useState(true);

  useEffect(() => {
    dispatch(fetchProjects({ page: 1, pageSize: 50 }));
    dispatch(fetchStarredProjects());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    setMetricsLoading(true);
    api.get('/dashboard/metrics')
      .then((res) => {
        if (!cancelled) setMetrics(res.data);
      })
      .catch(() => {
        if (!cancelled) setMetrics(null);
      })
      .finally(() => {
        if (!cancelled) setMetricsLoading(false);
      });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  // Read per-user visit history from localStorage
  const recentProjectIds = React.useMemo(() => {
    if (!user?.id) return [];
    try {
      return JSON.parse(localStorage.getItem(`recent-project-ids-${user.id}`) || '[]');
    } catch {
      return [];
    }
  }, [user?.id]); // eslint-disable-line

  // Sort projects: recently visited first, then fill remaining slots from fetched list
  const recentProjects = React.useMemo(() => {
    if (recentProjectIds.length === 0) return projects.slice(0, 4);
    const byId = {};
    projects.forEach(p => { byId[p.id] = p; });
    const visited = recentProjectIds.map(id => byId[id]).filter(Boolean);
    if (visited.length < 4) {
      const visitedSet = new Set(recentProjectIds);
      const rest = projects.filter(p => !visitedSet.has(p.id));
      visited.push(...rest);
    }
    return visited.slice(0, 4);
  }, [projects, recentProjectIds]);

  const getStatusBadge = (status) => {
    const classes = {
      active: 'badge-success',
      archived: 'badge-warning',
      on_hold: 'badge-danger',
    };
    return `badge ${classes[status] || 'badge-primary'}`;
  };

  const canGenerateProjectReport = ['super_admin', 'admin', 'manager'].includes(user?.role);

  const handleGenerateProjectReport = async (projectId) => {
    try {
      setGeneratingForProjectId(projectId);
      await generateAndDownloadReport({
        projectId,
        scope: 'project',
        setLoading: () => {},
      });
      toast.success('Project report generated successfully.');
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || 'Failed to generate project report.'
      );
    } finally {
      setGeneratingForProjectId(null);
    }
  };

  const handleToggleStar = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(toggleProjectStar({ projectId: project.id, isCurrentlyStarred: project.is_starred }));
  };

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            Welcome back, {user?.first_name}! Here's what's happening.
          </p>
        </div>
      </div>

      <div className="page-content">
        <GlobalTicketSearch />

        {/* Stats Grid */}
        <div className="stats-grid">
          <div className="stat-card stat-card--brand">
            <div className="stat-icon stat-icon--brand">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">
                {metricsLoading ? '—' : (metrics?.open_tasks ?? '—')}
              </span>
              <span className="stat-label">My Open Tasks</span>
            </div>
          </div>

          <div className="stat-card stat-card--danger">
            <div className="stat-icon stat-icon--danger">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm1 11H11V7h2v6zm0 4H11v-2h2v2z"/>
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">
                {metricsLoading ? '—' : (metrics?.overdue_tasks ?? '—')}
              </span>
              <span className="stat-label">Overdue Tasks</span>
            </div>
          </div>

          <div className="stat-card stat-card--success">
            <div className="stat-icon stat-icon--success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">
                {metricsLoading ? '—' : (metrics?.completed_this_week ?? '—')}
              </span>
              <span className="stat-label">Completed This Week</span>
            </div>
          </div>

          <div className="stat-card stat-card--warning">
            <div className="stat-icon stat-icon--warning">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <div className="stat-content">
              <span className="stat-value">
                {metricsLoading ? '—' : (metrics?.in_progress ?? '—')}
              </span>
              <span className="stat-label">Tasks In Progress</span>
            </div>
          </div>
        </div>

        {/* Starred Projects */}
        {starredProjects.length > 0 && (
          <div className="dashboard-section">
            <div className="section-header">
              <h2 className="starred-section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"
                     style={{ color: 'var(--color-warning)', flexShrink: 0 }}>
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Starred
              </h2>
            </div>
            <div className="projects-grid">
              {starredProjects.map((project) => (
                <div key={project.id} className="project-card">
                  <Link to={`/projects/${project.id}/board`} className="project-card-main">
                    <div className="project-card-header">
                      <div className="project-key">{project.key}</div>
                      <span className={getStatusBadge(project.status)}>{project.status}</span>
                    </div>
                    <h3 className="project-name">{project.name}</h3>
                    <p className="project-description">{project.description || 'No description'}</p>
                    <div className="project-stats">
                      <span>{project.task_count || 0} tasks</span>
                      <span>{project.member_count || 0} members</span>
                    </div>
                  </Link>
                  <div className="project-card-actions">
                    <button
                      className="star-btn starred"
                      onClick={(e) => handleToggleStar(e, project)}
                      title="Remove star"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-warning)' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Projects */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Projects</h2>
            <Link to="/projects" className="btn btn-ghost">View All</Link>
          </div>
          
          {loading ? (
            <div className="projects-grid">
              {[
                { title: 140, sub: 90  },
                { title: 110, sub: 110 },
                { title: 155, sub: 80  },
                { title: 125, sub: 100 },
              ].map((widths, i) => (
                <div key={i} className="dashboard-skeleton-card">
                  <div className="dashboard-skeleton-card-top">
                    <div className="sk sk-circle" style={{ width: 36, height: 36 }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div className="sk sk-line-lg" style={{ width: widths.title }} />
                      <div className="sk sk-line-sm" style={{ width: widths.sub }} />
                    </div>
                  </div>
                  <div className="dashboard-skeleton-card-foot">
                    <div className="sk sk-badge" style={{ width: 56 }} />
                    <div className="sk sk-line-sm" style={{ width: 70 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : recentProjects.length > 0 ? (
              <div className="projects-grid">
                {recentProjects.map((project) => (
                  <div 
                    key={project.id} 
                    className="project-card"
                  >
                    <Link 
                      to={`/projects/${project.id}/board`}
                      className="project-card-main"
                    >
                      <div className="project-card-header">
                        <div className="project-key">{project.key}</div>
                        <span className={getStatusBadge(project.status)}>
                          {project.status}
                        </span>
                        {project.is_starred && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"
                               style={{ color: 'var(--color-warning)', marginLeft: 'auto' }}>
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                          </svg>
                        )}
                      </div>
                      <h3 className="project-name">{project.name}</h3>
                      <p className="project-description">
                        {project.description || 'No description'}
                      </p>
                      <div className="project-stats">
                        <span>{project.task_count || 0} tasks</span>
                        <span>{project.member_count || 0} members</span>
                      </div>
                    </Link>
                    {canGenerateProjectReport && (
                      <div className="project-card-actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          style = {{ marginTop: '8px' }}
                          onClick={() => handleGenerateProjectReport(project.id)}
                          disabled={generatingForProjectId === project.id}
                        >
                          {generatingForProjectId === project.id ? 'Generating…' : 'Generate Report'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
          ) : (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor" opacity="0.3">
                <path d="M6 14a4 4 0 014-4h8l4 4h12a4 4 0 014 4v16a4 4 0 01-4 4H10a4 4 0 01-4-4V14z"/>
              </svg>
              <p>No projects yet</p>
              {['super_admin', 'admin'].includes(user?.role) && (
                <Link to="/projects" className="btn btn-primary">Create Project</Link>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions — only visible to roles that have at least one action */}
        {['super_admin', 'admin', 'manager'].includes(user?.role) && (
          <div className="dashboard-section">
            <h2>Quick Actions</h2>
            <div className="quick-actions">
              {['super_admin', 'admin'].includes(user?.role) && (
                <Link to="/projects" className="quick-action">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                  <span>New Project</span>
                </Link>
              )}
              {['super_admin', 'admin', 'manager'].includes(user?.role) && (
                <Link to="/users" className="quick-action">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  <span>Manage Users</span>
                </Link>
              )}
              {['super_admin', 'admin'].includes(user?.role) && (
                <Link to="/audit" className="quick-action">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                  </svg>
                  <span>Audit Logs</span>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
